import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import ReactPlayer from 'react-player/lazy';
import { useHistory, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { CircularProgress } from '@material-ui/core';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'timeago.js';

import Sidebar from '../../components/sidebar/Sidebar';
import Rightbar from '../../components/rightbar/Rightbar';
import BuyJGKToken from '../../components/buyJGKToken/BuyJGKToken';
import CashoutJGKToken from '../../components/cashoutJGKToken/CashoutJGKToken';
import { AuthContext } from '../../context/AuthContext';
import { useGetJGKTransactions } from '../../hooks/useGetJGKTransactions';
import { useGetCurrPlayer } from '../../hooks/useGetCurrPlayer';
import { useTheme } from '../../context/ThemeContext';
import { getLocalOpsConfig } from '../../utils/localOpsConfig';
import { useConfettiText } from '../../hooks/useConfettiText';
import { showToast } from '../../services/toastService';
import playSound from '../../utils/playSound';
import axiosConn from '../../axiosConn';
import { AirtimeTopUp } from '../airtime/Airtime';
import BuyerPendingConfirmationGateModal from '../../components/localops/BuyerPendingConfirmationGateModal';

import './jgktoken.css';

const HISTORY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'earned', label: 'Earned' },
  { key: 'spent', label: 'Spent' },
  { key: 'redeemed', label: 'Redeemed' },
];

const PREF_SOUND_KEY = 'jgkWalletSound';
const PREF_HAPTICS_KEY = 'jgkWalletHaptics';
const TOPUP_STARTED_KEY = 'jgkTopUpStartedAt';
const WALLET_SOUND_URL = '/sounds/success-chime.mp3';
const BUYER_PENDING_CONFIRMATION_CODE =
  'BUYER_PENDING_CONFIRMATION_REQUIRED';
const CASHOUT_QUICK_AMOUNTS_USD = [5, 10, 20, 50];

const formatJgk = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatUsd = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(numeric);
};

const extractBlockingOrderFromGatePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const source =
    payload?.data?.blockingOrder ||
    payload?.blockingOrder ||
    payload?.data?.data?.blockingOrder ||
    null;
  if (!source || typeof source !== 'object') return null;
  const orderId = source?.orderId || source?.id || source?._id || null;
  const offeringId =
    source?.offeringId ||
    source?.offering?._id ||
    (typeof source?.offering === 'string' ? source.offering : null) ||
    null;
  if (!orderId) return null;
  return {
    ...source,
    orderId: String(orderId),
    offeringId: offeringId ? String(offeringId) : null,
  };
};

export default function JGKToken() {
  const { player } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const location = useLocation();
  const history = useHistory();
  const { darkTheme } = useTheme();
  const mobileView = useMediaQuery({
    query: '(max-width: 805px)',
  });
  const [activePanel, setActivePanel] = useState('topup');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [showJGKTutorial, setShowJGKTutorial] = useState(false);
  const [cashoutRequestOpen, setCashoutRequestOpen] = useState(false);
  const [cashoutMethod, setCashoutMethod] = useState('venmo');
  const [cashoutAmount, setCashoutAmount] = useState('');
  const [cashoutHandle, setCashoutHandle] = useState('');
  const [cashoutError, setCashoutError] = useState('');
  const [cashoutLoading, setCashoutLoading] = useState(false);
  const [pendingConfirmationGate, setPendingConfirmationGate] = useState(null);
  const pendingConfirmationGateBusyRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(PREF_SOUND_KEY) !== 'off';
  });
  const [hapticsEnabled, setHapticsEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(PREF_HAPTICS_KEY) !== 'off';
  });
  const confettiRef = useRef(null);
  const getPaidRef = useRef(null);
  const panelRef = useRef(null);

  const config = useMemo(() => getLocalOpsConfig(), []);

  const { data: currPlayer, isLoading: isPlayerLoading } = useGetCurrPlayer(
    () => {},
    (err) => console.log(err),
    player,
  );

  const {
    data: jgkData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetJGKTransactions(player, () => {}, (err) => console.log(err));

  const flattenedData = useMemo(
    () => (jgkData?.pages ? jgkData.pages.flat() : []),
    [jgkData],
  );

  const currentPlayerId = player?.data?.player?.playerId;
  const currentPlayerMongoId = player?.data?.player?._id;

  const resolveHistoryCategory = useCallback(
    (tx) => {
      const type = `${tx?.type || ''}`.toLowerCase();
      if (
        type.includes('cashout') ||
        type.includes('redeem') ||
        type.includes('gift')
      ) {
        return 'redeemed';
      }

      const amountRaw =
        Number.isFinite(tx?.amount) || typeof tx?.amount === 'number'
          ? tx.amount
          : tx?.amountJGK;
      const amount = Number(amountRaw);
      if (Number.isFinite(amount)) {
        if (amount < 0) return 'spent';
        if (amount > 0) return 'earned';
      }

      if (
        type.includes('send') ||
        type.includes('transfer') ||
        type.includes('payment')
      ) {
        return 'spent';
      }

      const sender = tx?.sender ? String(tx.sender) : '';
      if (
        sender &&
        (sender === currentPlayerId || sender === currentPlayerMongoId)
      ) {
        return 'spent';
      }
      return 'earned';
    },
    [currentPlayerId, currentPlayerMongoId],
  );

  const filteredHistory = useMemo(() => {
    if (historyFilter === 'all') return flattenedData;
    return flattenedData.filter(
      (tx) => resolveHistoryCategory(tx) === historyFilter,
    );
  }, [flattenedData, historyFilter, resolveHistoryCategory]);

  const balance = Number(currPlayer?.jgkTokens || 0);
  const usdEstimate = useMemo(() => {
    const rate = config.jgkPerUsd || 1000;
    if (!Number.isFinite(balance) || rate <= 0) return 0;
    return balance / rate;
  }, [balance, config.jgkPerUsd]);

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    confettiRef,
    mobileView,
    'Balance updated',
    140,
    'jgk',
  );

  const walletTitle = useMemo(() => {
    switch (activePanel) {
      case 'topup':
        return 'Top Up';
      case 'redeem':
        return 'Redeem';
      case 'send':
        return 'Send';
      case 'airtime':
        return 'Mobile Airtime';
      case 'history':
        return 'History';
      default:
        return 'Wallet';
    }
  }, [activePanel]);

  const renderHistoryIcon = (category) => {
    if (category === 'redeemed') return <RedeemRoundedIcon fontSize="small" />;
    if (category === 'spent') return <ArrowUpwardRoundedIcon fontSize="small" />;
    return <ArrowDownwardRoundedIcon fontSize="small" />;
  };

  const formatHistoryLabel = (tx, category) => {
    if (tx?.type) return tx.type;
    if (category === 'redeemed') return 'Redeemed JGK';
    if (category === 'spent') return 'Sent JGK';
    return 'Earned JGK';
  };

  const formatHistoryMeta = (tx) => {
    const sender = tx?.sender ? `Sender: ${tx.sender}` : null;
    const when = tx?.createdAt ? format(tx.createdAt) : null;
    return [sender, when].filter(Boolean).join(' - ') || 'Activity';
  };

  const triggerFeedback = useCallback(
    (context = 'action') => {
      if (soundEnabled) {
        playSound(WALLET_SOUND_URL, 1.6);
      }
      if (hapticsEnabled && navigator?.vibrate) {
        navigator.vibrate(context === 'success' ? [40, 30, 40] : 25);
      }
    },
    [soundEnabled, hapticsEnabled],
  );

  const waitForTopUpLedger = useCallback(
    async (startedAtMs) => {
      if (!player?.token || !currentPlayerMongoId) return false;
      const startAt = Number(startedAtMs) || Date.now();
      for (let attempt = 0; attempt < 12; attempt += 1) {
        try {
          const res = await axiosConn.get(
            `jgkTokenTransactions/${currentPlayerMongoId}`,
            {
              headers: { Authorization: `Bearer ${player.token}` },
              params: { page: 1, limit: 10 },
            },
          );
          const txs = Array.isArray(res?.data) ? res.data : [];
          const found = txs.some((tx) => {
            const createdAt = Date.parse(tx?.createdAt || '');
            const isPurchase = String(tx?.type || '').toLowerCase() === 'purchase';
            return isPurchase && Number.isFinite(createdAt) && createdAt >= startAt - 5000;
          });
          if (found) return true;
        } catch (err) {
          // ignore and retry
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      return false;
    },
    [player?.token, currentPlayerMongoId],
  );

  const focusGetPaid = useCallback(() => {
    if (!getPaidRef.current) return;
    getPaidRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const CASHOUT_FEE_BPS = Number(process.env.REACT_APP_CASHOUT_FEE_BPS || 0);
  const feePercent = Number.isFinite(CASHOUT_FEE_BPS)
    ? CASHOUT_FEE_BPS / 100
    : 0;
  const cashoutMinUsd = useMemo(() => {
    const raw = Number(process.env.REACT_APP_CASHOUT_MIN_USD);
    return Number.isFinite(raw) && raw > 0 ? raw : 5;
  }, []);
  const cashoutMinJGK = useMemo(
    () => Math.round(cashoutMinUsd * (config.jgkPerUsd || 1000)),
    [cashoutMinUsd, config.jgkPerUsd],
  );
  const computeCashoutFee = useCallback(
    (amount) => {
      const amt = Number(amount || 0);
      if (!Number.isFinite(amt) || amt <= 0) {
        return { feeJGK: 0, netJGK: 0, grossJGK: 0 };
      }
      const feeJGK = Math.round((amt * CASHOUT_FEE_BPS) / 10000);
      const netJGK = Math.max(0, amt - feeJGK);
      return { feeJGK, netJGK, grossJGK: amt };
    },
    [CASHOUT_FEE_BPS],
  );
  const cashoutAmountUsd = Number(cashoutAmount || 0);
  const cashoutAmountJGK =
    Number.isFinite(cashoutAmountUsd) && cashoutAmountUsd > 0
      ? Math.round(cashoutAmountUsd * (config.jgkPerUsd || 1000))
      : 0;
  const cashoutFeePreview = computeCashoutFee(cashoutAmountJGK);
  const cashoutNetUsd =
    cashoutFeePreview.netJGK > 0
      ? cashoutFeePreview.netJGK / (config.jgkPerUsd || 1000)
      : 0;

  const focusPanel = useCallback(() => {
    if (!panelRef.current) return;
    panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleOpenCashoutRequest = useCallback((method = 'venmo') => {
    setCashoutMethod(method);
    setCashoutAmount('');
    setCashoutHandle('');
    setCashoutError('');
    setCashoutLoading(false);
    setPendingConfirmationGate(null);
    setCashoutRequestOpen(true);
  }, []);

  const handleCloseCashoutRequest = useCallback(() => {
    if (cashoutLoading) return;
    if (pendingConfirmationGateBusyRef.current) return;
    setPendingConfirmationGate(null);
    setCashoutRequestOpen(false);
  }, [cashoutLoading]);

  const handleSubmitCashoutRequest = useCallback(async () => {
    const amountUsd = Number(cashoutAmount);
    const amountJGK =
      Number.isFinite(amountUsd) && amountUsd > 0
        ? Math.round(amountUsd * (config.jgkPerUsd || 1000))
        : 0;
    const trimmedHandle = String(cashoutHandle || '').trim();
    if (!Number.isFinite(amountUsd) || amountUsd <= 0 || amountJGK <= 0) {
      setCashoutError('Enter a valid cashout amount in dollars.');
      return;
    }
    if (amountJGK < cashoutMinJGK) {
      setCashoutError(
        `Minimum cashout is ${formatUsd(cashoutMinUsd)} (${formatJgk(
          cashoutMinJGK,
        )}).`,
      );
      return;
    }
    if (Number.isFinite(balance) && amountJGK > balance) {
      setCashoutError('Amount exceeds your JGK Balance.');
      return;
    }
    if (!trimmedHandle) {
      setCashoutError('Add your payout handle to continue.');
      return;
    }
    setCashoutError('');
    setCashoutLoading(true);
    try {
      await axiosConn.post(
        'jgk/cashout-request',
        {
          amountJGK,
          method: cashoutMethod,
          handle: trimmedHandle,
        },
        {
          headers: { Authorization: `Bearer ${player?.token}` },
        },
      );
      setCashoutLoading(false);
      setCashoutRequestOpen(false);
      queryClient.invalidateQueries({ queryKey: ['getCurrPlayer'] });
      queryClient.invalidateQueries({ queryKey: ['jgkTransactions'] });
      showToast({
        type: 'success',
        title: 'Cashout requested. JGK reserved.',
        message:
          feePercent > 0
            ? `We've reserved ${formatJgk(
                amountJGK,
              )} for a ${formatUsd(
                amountUsd,
              )} cashout. You will receive ${formatUsd(
                computeCashoutFee(amountJGK).netJGK /
                  (config.jgkPerUsd || 1000),
              )} after a ${feePercent}% fee.`
            : `We've reserved ${formatJgk(
                amountJGK,
              )} for a ${formatUsd(amountUsd)} cashout.`,
      });
      triggerFeedback('success');
    } catch (err) {
      setCashoutLoading(false);
      const payload = err?.response?.data;
      if (payload?.code === BUYER_PENDING_CONFIRMATION_CODE) {
        const blockingOrder = extractBlockingOrderFromGatePayload(payload);
        setPendingConfirmationGate({
          gateId: `cashout_gate_${Date.now()}`,
          context: 'cashout',
          intent: {
            amountJGK,
            method: cashoutMethod,
            handle: trimmedHandle,
          },
          blockingOrder,
          busy: false,
          error: '',
        });
        return;
      }
      setCashoutError(
        payload?.message ||
          'We could not send your request. Please try again.',
      );
      console.error('[cashout-request][client]', {
        status: err?.response?.status,
        message: payload?.message,
        data: payload,
      });
    }
  }, [
    cashoutAmount,
    cashoutHandle,
    cashoutMethod,
    balance,
    player?.token,
    queryClient,
    triggerFeedback,
    feePercent,
    computeCashoutFee,
    config.jgkPerUsd,
    cashoutMinJGK,
    cashoutMinUsd,
  ]);

  const handleMessageSellerFromGate = useCallback(
    async (blockingOrder) => {
      const sellerId =
        blockingOrder?.seller?._id ||
        blockingOrder?.sellerId ||
        blockingOrder?.seller ||
        null;
      const currentPlayerId = player?.data?.player?._id || player?._id;
      if (!sellerId || !currentPlayerId || !player?.token) {
        setCashoutError('Unable to message the seller right now.');
        return;
      }
      if (String(sellerId) === String(currentPlayerId)) {
        setCashoutError('This is your own order.');
        return;
      }
      try {
        const created = await axiosConn.post(
          'conversations/get-or-create',
          { senderId: currentPlayerId, receiverId: sellerId },
          { headers: { Authorization: `Bearer ${player.token}` } },
        );
        const convoId =
          created?.data?._id ||
          created?.data?.conversationId ||
          created?.data?.id ||
          null;
        if (convoId) {
          setPendingConfirmationGate(null);
          history.push(`/messenger/${convoId}`);
        } else {
          setCashoutError('Unable to start a message right now.');
        }
      } catch (err) {
        setCashoutError('Unable to start a message right now.');
      }
    },
    [history, player],
  );

  const handleResolvePendingConfirmationGate = useCallback(
    async (action) => {
      const gate = pendingConfirmationGate;
      if (!gate || gate.context !== 'cashout') return;
      if (pendingConfirmationGateBusyRef.current) return;
      const gateId = gate.gateId;
      const blockingOrder = gate.blockingOrder || {};

      if (action === 'message') {
        await handleMessageSellerFromGate(blockingOrder);
        return;
      }

      pendingConfirmationGateBusyRef.current = true;
      setPendingConfirmationGate((prev) =>
        prev && prev.gateId === gateId
          ? { ...prev, busy: true, error: '' }
          : prev,
      );

      try {
        if (!blockingOrder?.orderId) {
          throw new Error('Missing blocking order id.');
        }
        const endpoint = action === 'confirm' ? 'confirm' : 'dispute';
        await axiosConn.post(
          `local-ops/orders/${blockingOrder.orderId}/${endpoint}`,
          {},
          {
            headers: { Authorization: `Bearer ${player?.token}` },
          },
        );

        setPendingConfirmationGate((prev) =>
          prev && prev.gateId === gateId ? null : prev,
        );
        await handleSubmitCashoutRequest();
      } catch (err) {
        const payload = err?.response?.data;
        if (payload?.code === BUYER_PENDING_CONFIRMATION_CODE) {
          const blocking = extractBlockingOrderFromGatePayload(payload);
          setPendingConfirmationGate({
            gateId: `cashout_gate_${Date.now()}`,
            context: 'cashout',
            intent: gate.intent,
            blockingOrder: blocking,
            busy: false,
            error: '',
          });
        } else {
          setPendingConfirmationGate((prev) =>
            prev && prev.gateId === gateId
              ? {
                  ...prev,
                  error:
                    payload?.message ||
                    err?.message ||
                    'Unable to resolve this order right now.',
                }
              : prev,
          );
        }
      } finally {
        pendingConfirmationGateBusyRef.current = false;
        setPendingConfirmationGate((prev) =>
          prev && prev.gateId === gateId ? { ...prev, busy: false } : prev,
        );
      }
    },
    [pendingConfirmationGate, handleMessageSellerFromGate, handleSubmitCashoutRequest, player?.token],
  );

  useEffect(() => {
    const hash = location?.hash || '';
    if (!hash) return;
    if (hash === '#cashout') {
      handleOpenCashoutRequest();
      focusGetPaid();
      return;
    }
    if (hash === '#getpaid') {
      focusGetPaid();
      return;
    }
    if (hash === '#redeem') {
      setActivePanel('redeem');
      focusPanel();
      return;
    }
    if (hash === '#airtime') {
      setActivePanel('airtime');
      focusPanel();
    }
  }, [location?.hash, handleOpenCashoutRequest, focusGetPaid, focusPanel]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasSuccess =
      params.get('success') === 'true' ||
      params.get('checkout') === 'success' ||
      params.get('stripe') === 'success' ||
      params.has('session_id');
    if (!hasSuccess) return;

    let canceled = false;
    const startedAt =
      Number(window?.localStorage?.getItem(TOPUP_STARTED_KEY)) || Date.now();

    showToast({
      type: 'info',
      title: 'Top-up pending',
      message: 'Updating your JGK Balance...',
    });

    waitForTopUpLedger(startedAt).then((confirmed) => {
      if (canceled) return;
      if (confirmed) {
        showToast({
          type: 'success',
          title: 'Balance updated',
          message: 'Your JGK Balance just increased.',
        });
        triggerFeedback('success');
        showConfettiText();
        window?.localStorage?.removeItem(TOPUP_STARTED_KEY);
      }
    });

    window.history.replaceState({}, document.title, window.location.pathname);

    return () => {
      canceled = true;
    };
  }, [showConfettiText, triggerFeedback, waitForTopUpLedger]);

  const sparkline = useMemo(() => {
    const ordered = [...flattenedData]
      .filter((tx) => tx?.createdAt)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .slice(-18);
    if (ordered.length < 2) return null;

    let running = 0;
    const values = ordered.map((tx) => {
      const amount = Number(tx?.amount || 0);
      const category = resolveHistoryCategory(tx);
      running += category === 'spent' || category === 'redeemed' ? -amount : amount;
      return running;
    });

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const width = 140;
    const height = 40;
    const step = width / (values.length - 1);
    const points = values.map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    });

    return {
      path: `M ${points.join(' L ')}`,
      lastPoint: points[points.length - 1],
      width,
      height,
    };
  }, [flattenedData, resolveHistoryCategory]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PREF_SOUND_KEY, soundEnabled ? 'on' : 'off');
  }, [soundEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PREF_HAPTICS_KEY, hapticsEnabled ? 'on' : 'off');
  }, [hapticsEnabled]);

  return (
    <>
      <div className="jgktokenContainer">
        {!mobileView && <Sidebar />}
        <div
          className={
            darkTheme
              ? 'dark-theme blackBg createJGTokenWrapper'
              : 'createJGTokenWrapper'
          }
        >
          <div className="jgk-wallet">
            <div className="jgk-wallet__header">
              <div className="jgk-wallet__title">
                <AccountBalanceWalletOutlinedIcon />
                <span>JGK Wallet</span>
              </div>
              <div className="jgk-wallet__subtitle">
                You can earn, spend, or cash out your JGK Balance anytime.
              </div>
            </div>

            <div className="jgk-wallet__hero">
              <div className="jgk-wallet__balance-card">
                <div className="jgk-wallet__balance-label">JGK Balance</div>
                <div className="jgk-wallet__balance-amount">
                  {isPlayerLoading ? (
                    <CircularProgress color="inherit" size="20px" />
                  ) : (
                    `JGK ${formatJgk(balance)}`
                  )}
                </div>
                <div className="jgk-wallet__balance-usd">
                  {`About ${formatUsd(usdEstimate)}`}
                </div>
                <div className="jgk-wallet__balance-meta">
                  {`${config.jgkPerUsd} JGK = $1`}
                </div>
                <div className="jgk-wallet__balance-meta">
                  Earned from real local actions.
                </div>
                {sparkline ? (
                  <div className="jgk-wallet__sparkline">
                    <svg
                      width={sparkline.width}
                      height={sparkline.height}
                      viewBox={`0 0 ${sparkline.width} ${sparkline.height}`}
                      aria-hidden="true"
                    >
                      <path
                        d={sparkline.path}
                        className="jgk-wallet__sparkline-path"
                      />
                      {sparkline.lastPoint ? (
                        <circle
                          className="jgk-wallet__sparkline-dot"
                          cx={sparkline.lastPoint.split(',')[0]}
                          cy={sparkline.lastPoint.split(',')[1]}
                          r="3"
                        />
                      ) : null}
                    </svg>
                    <span className="jgk-wallet__sparkline-label">
                      Recent activity
                      <span
                        className="jgk-wallet__sparkline-info"
                        title="Based on your most recent transactions."
                        aria-label="Based on your most recent transactions."
                      >
                        i
                      </span>
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="jgk-wallet__profile">
                <img
                  className="jgk-wallet__avatar"
                  src={
                    currPlayer?.profilePhoto ||
                    'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png'
                  }
                  alt=""
                  loading="lazy"
                />
                <div className="jgk-wallet__profile-meta">
                  <div className="jgk-wallet__profile-id">
                    {currPlayer?.playerId || currentPlayerId || 'JG Player'}
                  </div>
                  <div className="jgk-wallet__profile-sub">
                    You're always in control of your balance.
                  </div>
                </div>
              </div>
            </div>

            <div className="jgk-wallet__getpaid" ref={getPaidRef}>
              <div className="jgk-wallet__getpaid-header">
                <div className="jgk-wallet__getpaid-title">Get paid</div>
                <div className="jgk-wallet__getpaid-sub">
                  You can use your JGK Balance or cash out anytime.
                </div>
              </div>
              <div className="jgk-wallet__getpaid-grid">
                <button
                  type="button"
                  className="jgk-wallet__getpaid-card"
                  onClick={() => {
                    setActivePanel('redeem');
                    focusPanel();
                  }}
                >
                  <span className="jgk-wallet__getpaid-icon">
                    <CardGiftcardOutlinedIcon />
                  </span>
                  <span className="jgk-wallet__getpaid-body">
                    <span className="jgk-wallet__getpaid-row">
                      <span className="jgk-wallet__getpaid-name">
                        Gift Cards
                      </span>
                      <span className="jgk-wallet__getpaid-speed">Instant</span>
                    </span>
                    <span className="jgk-wallet__getpaid-copy">
                      Get paid instantly with Amazon, Walmart, and more.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="jgk-wallet__getpaid-card"
                  onClick={() => {
                    setActivePanel('airtime');
                    focusPanel();
                  }}
                >
                  <span className="jgk-wallet__getpaid-icon">
                    <PhoneIphoneOutlinedIcon />
                  </span>
                  <span className="jgk-wallet__getpaid-body">
                    <span className="jgk-wallet__getpaid-row">
                      <span className="jgk-wallet__getpaid-name">
                        Mobile Airtime
                      </span>
                      <span className="jgk-wallet__getpaid-speed">Instant</span>
                    </span>
                    <span className="jgk-wallet__getpaid-copy">
                      Top up your phone instantly.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="jgk-wallet__getpaid-card"
                  onClick={() => handleOpenCashoutRequest()}
                >
                  <span className="jgk-wallet__getpaid-icon">
                    <PaymentsOutlinedIcon />
                  </span>
                  <span className="jgk-wallet__getpaid-body">
                    <span className="jgk-wallet__getpaid-row">
                      <span className="jgk-wallet__getpaid-name">
                        Cash App / Venmo / Zelle
                      </span>
                      <span className="jgk-wallet__getpaid-speed">Daily</span>
                    </span>
                    <span className="jgk-wallet__getpaid-copy">
                      Request a cashout and get paid within 24 hours.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="jgk-wallet__getpaid-card is-disabled"
                  disabled
                >
                  <span className="jgk-wallet__getpaid-icon">
                    <AccountBalanceOutlinedIcon />
                  </span>
                  <span className="jgk-wallet__getpaid-body">
                    <span className="jgk-wallet__getpaid-row">
                      <span className="jgk-wallet__getpaid-name">
                        Bank / Debit
                      </span>
                      <span className="jgk-wallet__getpaid-speed">
                        Coming soon
                      </span>
                    </span>
                    <span className="jgk-wallet__getpaid-copy">
                      Automatic payouts are on the way.
                    </span>
                  </span>
                </button>
              </div>
              <div className="jgk-wallet__getpaid-trust">
                Cash payouts start at {formatUsd(cashoutMinUsd)}. No hidden
                fees. You're always in control of your balance.
              </div>
            </div>

            <div className="jgk-wallet__prefs">
              <span className="jgk-wallet__prefs-label">Feedback</span>
              <button
                type="button"
                className={`jgk-wallet__pref ${soundEnabled ? 'is-on' : ''}`}
                onClick={() => setSoundEnabled((prev) => !prev)}
              >
                Sound
              </button>
              <button
                type="button"
                className={`jgk-wallet__pref ${hapticsEnabled ? 'is-on' : ''}`}
                onClick={() => setHapticsEnabled((prev) => !prev)}
              >
                Haptics
              </button>
            </div>

            <div className="jgk-wallet__learn">
              <button
                type="button"
                className="jgk-wallet__learn-button"
                onClick={() => setShowJGKTutorial((prev) => !prev)}
              >
                <InfoOutlinedIcon fontSize="small" />
                {showJGKTutorial ? 'Hide JGK 101' : 'JGK 101'}
              </button>
              {showJGKTutorial && (
                <div className="jgk-wallet__learn-card">
                  <div className="jgk-wallet__learn-video">
                    <ReactPlayer
                      autoPlay={false}
                      loop={false}
                      muted={true}
                      width="100%"
                      playing={false}
                      controls={true}
                      playbackRate={1.25}
                      url="https://res.cloudinary.com/joegalaxy/video/upload/v1688862323/CashoutJGKToGiftCardv1_hsy6nd.mp4"
                    />
                  </div>
                  <div className="jgk-wallet__learn-text">
                    <p>
                      JGK is Joe Galaxy Klout. You earn it by posting, sharing,
                      and helping others. Use it to support friends or redeem
                      for gift cards and airtime.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="jgk-wallet__actions">
              <button
                ref={confettiRef}
                type="button"
                className={`jgk-wallet__action ${
                  activePanel === 'topup' ? 'is-active' : ''
                }`}
                onClick={() => setActivePanel('topup')}
              >
                <span className="jgk-wallet__action-icon">
                  <AddCircleOutlineIcon />
                </span>
                <span className="jgk-wallet__action-title">Top Up</span>
                <span className="jgk-wallet__action-sub">
                  Add JGK balance instantly
                </span>
              </button>
              <button
                type="button"
                className={`jgk-wallet__action ${
                  activePanel === 'redeem' ? 'is-active' : ''
                }`}
                onClick={() => setActivePanel('redeem')}
              >
                <span className="jgk-wallet__action-icon">
                  <CardGiftcardOutlinedIcon />
                </span>
                <span className="jgk-wallet__action-title">Redeem</span>
                <span className="jgk-wallet__action-sub">
                  Turn JGK into gift cards
                </span>
              </button>
              <button
                type="button"
                className={`jgk-wallet__action ${
                  activePanel === 'send' ? 'is-active' : ''
                }`}
                onClick={() => setActivePanel('send')}
              >
                <span className="jgk-wallet__action-icon">
                  <SendRoundedIcon />
                </span>
                <span className="jgk-wallet__action-title">Send</span>
                <span className="jgk-wallet__action-sub">
                  Share JGK balance with friends
                </span>
              </button>
              <button
                type="button"
                className={`jgk-wallet__action ${
                  activePanel === 'history' ? 'is-active' : ''
                }`}
                onClick={() => setActivePanel('history')}
              >
                <span className="jgk-wallet__action-icon">
                  <HistoryRoundedIcon />
                </span>
                <span className="jgk-wallet__action-title">History</span>
                <span className="jgk-wallet__action-sub">
                  Track every JGK move
                </span>
              </button>
            </div>

            <div className="jgk-wallet__panel" ref={panelRef}>
              <div className="jgk-wallet__panel-header">
                <div className="jgk-wallet__panel-title">{walletTitle}</div>
                <div className="jgk-wallet__panel-sub">
                  {activePanel === 'topup' &&
                    'Secure Stripe checkout in seconds.'}
                  {activePanel === 'redeem' &&
                    'Choose a gift card and confirm your redemption.'}
                  {activePanel === 'send' &&
                    'Pick a friend and send JGK instantly.'}
                  {activePanel === 'airtime' &&
                    'Top up your phone instantly with JGK.'}
                  {activePanel === 'history' &&
                    'A full history of your JGK Balance activity.'}
                </div>
              </div>

              {activePanel === 'topup' && (
                <div className="jgk-wallet__panel-body">
                  <BuyJGKToken onCheckout={() => triggerFeedback('action')} />
                </div>
              )}

              {activePanel === 'redeem' && (
                <div className="jgk-wallet__panel-body">
                  <CashoutJGKToken
                    mode="redeem"
                    embedded={true}
                    showJGKTutorial={showJGKTutorial}
                    SetShowJGKTutorial={setShowJGKTutorial}
                    onRedeemSuccess={() => {
                      triggerFeedback('success');
                      showToast({
                        type: 'success',
                        title: 'Redeem confirmed',
                        message: 'Your gift card is on the way.',
                      });
                    }}
                  />
                </div>
              )}

              {activePanel === 'send' && (
                <div className="jgk-wallet__panel-body">
                  <CashoutJGKToken
                    mode="send"
                    embedded={true}
                    onSendSuccess={() => {
                      triggerFeedback('success');
                      showToast({
                        type: 'success',
                        title: 'JGK sent',
                        message: 'Your transfer is complete.',
                      });
                    }}
                  />
                </div>
              )}

              {activePanel === 'airtime' && (
                <div className="jgk-wallet__panel-body">
                  <AirtimeTopUp embedded />
                </div>
              )}

              {activePanel === 'history' && (
                <div className="jgk-wallet__panel-body">
                  <div className="jgk-wallet__filters">
                    {HISTORY_FILTERS.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        className={`jgk-wallet__filter ${
                          historyFilter === filter.key ? 'is-active' : ''
                        }`}
                        onClick={() => setHistoryFilter(filter.key)}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                  <div className="jgk-wallet__history">
                    {filteredHistory.length === 0 && (
                      <div className="jgk-wallet__empty">
                        No activity yet. Your JGK history will show here.
                      </div>
                    )}
                    {filteredHistory.map((tx, index) => {
                      const category = resolveHistoryCategory(tx);
                      const amount = Number(tx?.amount || 0);
                      const isDebit =
                        category === 'spent' || category === 'redeemed';
                      const sign = isDebit ? '-' : '+';
                      return (
                        <div className="jgk-wallet__history-item" key={index}>
                          <div
                            className={`jgk-wallet__history-icon is-${category}`}
                          >
                            {renderHistoryIcon(category)}
                          </div>
                          <div className="jgk-wallet__history-body">
                            <div className="jgk-wallet__history-title">
                              {formatHistoryLabel(tx, category)}
                            </div>
                            <div className="jgk-wallet__history-sub">
                              {formatHistoryMeta(tx)}
                            </div>
                          </div>
                          <div
                            className={`jgk-wallet__history-amount ${
                              isDebit ? 'is-debit' : 'is-credit'
                            }`}
                          >
                            {`${sign} JGK ${formatJgk(Math.abs(amount))}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {hasNextPage && (
                    <button
                      type="button"
                      className="jgk-wallet__load-more"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {cashoutRequestOpen && (
              <div className="jgk-wallet-modal">
                <div
                  className="jgk-wallet-modal__backdrop"
                  onClick={handleCloseCashoutRequest}
                />
                <div className="jgk-wallet-modal__card">
                  <div className="jgk-wallet-modal__title">
                    Request cashout
                  </div>
                  <div className="jgk-wallet-modal__helper">
                    Choose how you want to receive your payout.
                  </div>
                  <div className="jgk-wallet-modal__options">
                    <button
                      type="button"
                      className={`jgk-wallet-modal__option ${
                        cashoutMethod === 'cashapp' ? 'is-active' : ''
                      }`}
                      onClick={() => {
                        setCashoutMethod('cashapp');
                        setCashoutError('');
                      }}
                      aria-pressed={cashoutMethod === 'cashapp'}
                      disabled={cashoutLoading}
                    >
                      Cash App
                    </button>
                    <button
                      type="button"
                      className={`jgk-wallet-modal__option ${
                        cashoutMethod === 'venmo' ? 'is-active' : ''
                      }`}
                      onClick={() => {
                        setCashoutMethod('venmo');
                        setCashoutError('');
                      }}
                      aria-pressed={cashoutMethod === 'venmo'}
                      disabled={cashoutLoading}
                    >
                      Venmo
                    </button>
                    <button
                      type="button"
                      className={`jgk-wallet-modal__option ${
                        cashoutMethod === 'zelle' ? 'is-active' : ''
                      }`}
                      onClick={() => {
                        setCashoutMethod('zelle');
                        setCashoutError('');
                      }}
                      aria-pressed={cashoutMethod === 'zelle'}
                      disabled={cashoutLoading}
                    >
                      Zelle
                    </button>
                  </div>
                  <div className="jgk-wallet-modal__field">
                    <label
                      htmlFor="jgkCashoutAmount"
                      className="jgk-wallet-modal__label"
                    >
                      Cashout amount (USD)
                    </label>
                    <div className="jgk-wallet-modal__quick">
                      {CASHOUT_QUICK_AMOUNTS_USD.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          className={`jgk-wallet-modal__quick-button ${
                            Number(cashoutAmount) === amount ? 'is-active' : ''
                          }`}
                          onClick={() => {
                            setCashoutAmount(String(amount));
                            if (cashoutError) setCashoutError('');
                          }}
                          disabled={cashoutLoading}
                        >
                          {formatUsd(amount)}
                        </button>
                      ))}
                    </div>
                    <input
                      id="jgkCashoutAmount"
                      type="number"
                      min={cashoutMinUsd}
                      step="0.01"
                      inputMode="decimal"
                      className="jgk-wallet-modal__input"
                      value={cashoutAmount}
                      onChange={(event) => {
                        setCashoutAmount(event.target.value);
                        if (cashoutError) setCashoutError('');
                      }}
                      placeholder="e.g. 17.00"
                      disabled={cashoutLoading}
                    />
                  </div>
                  <div className="jgk-wallet-modal__note">
                    Minimum cashout: {formatUsd(cashoutMinUsd)} (
                    {formatJgk(cashoutMinJGK)}). Your JGK will be reserved and
                    cannot be spent while the request is pending.
                  </div>
                  <div className="jgk-wallet-modal__preview">
                    <strong>
                      {cashoutAmountJGK > 0
                        ? `${formatUsd(cashoutAmountUsd)} = ${formatJgk(
                            cashoutAmountJGK,
                          )}`
                        : `Enter ${formatUsd(cashoutMinUsd)} or more.`}
                    </strong>
                    {cashoutAmountJGK > 0 && (
                      <span>
                        This is the JGK amount that will be reserved from your
                        balance.
                      </span>
                    )}
                  </div>
                  <div className="jgk-wallet-modal__field">
                    <label
                      htmlFor="jgkCashoutHandle"
                      className="jgk-wallet-modal__label"
                    >
                      {`Your ${
                        cashoutMethod === 'venmo'
                          ? 'Venmo'
                          : cashoutMethod === 'zelle'
                          ? 'Zelle'
                          : 'Cash App'
                      } handle`}
                    </label>
                    <input
                      id="jgkCashoutHandle"
                      type="text"
                      className="jgk-wallet-modal__input"
                      value={cashoutHandle}
                      onChange={(event) => {
                        setCashoutHandle(event.target.value);
                        if (cashoutError) setCashoutError('');
                      }}
                      placeholder="@yourname"
                      disabled={cashoutLoading}
                    />
                  </div>
                  {cashoutError && (
                    <div className="jgk-wallet__error">{cashoutError}</div>
                  )}
                  <div className="jgk-wallet-modal__note">
                    Cashout fee: {feePercent}%<br />
                    You will receive: {formatUsd(cashoutNetUsd)} (
                    {formatJgk(cashoutFeePreview.netJGK)})<br />
                    Fee: {formatUsd(
                      cashoutFeePreview.feeJGK / (config.jgkPerUsd || 1000),
                    )}{' '}
                    ({formatJgk(cashoutFeePreview.feeJGK)})
                  </div>
                  <div className="jgk-wallet-modal__actions">
                    <button
                      type="button"
                      className="jgk-wallet-modal__cancel"
                      onClick={handleCloseCashoutRequest}
                      disabled={cashoutLoading}
                    >
                      Not now
                    </button>
                    <button
                      type="button"
                      className="jgk-wallet-modal__confirm"
                      onClick={handleSubmitCashoutRequest}
                      disabled={cashoutLoading}
                    >
                      {cashoutLoading ? (
                        <CircularProgress color="inherit" size="18px" />
                      ) : (
                        'Request cashout'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <BuyerPendingConfirmationGateModal
              open={cashoutRequestOpen && !!pendingConfirmationGate}
              contextLabel="Before requesting cashout"
              blockingOrder={pendingConfirmationGate?.blockingOrder || null}
              busy={pendingConfirmationGate?.busy === true}
              error={pendingConfirmationGate?.error || ''}
              onClose={() => {
                if (pendingConfirmationGateBusyRef.current) return;
                setPendingConfirmationGate(null);
              }}
              onConfirm={() => handleResolvePendingConfirmationGate('confirm')}
              onDispute={() => handleResolvePendingConfirmationGate('dispute')}
              onMessage={() => handleResolvePendingConfirmationGate('message')}
            />
            <ConfettiTextComponent />
          </div>
        </div>
        {!mobileView && <Rightbar />}
      </div>
    </>
  );
}
